/**
 * <plw-story-quotes> — approved share-your-story submissions on the Stories
 * listing (snippets/plw-story-band.liquid).
 *
 * Fetches records client-side from Airtable (the section's READ-ONLY token —
 * scoped to data.records:read on the dedicated Stories base, never the form's
 * write token) filtered to `Approved` records. ONE request per page serves
 * every band (client QA 2026-08-14): the first band near the viewport fetches
 * all approved records (Story + Prompt fields) into a module-level shared
 * promise, and each band filters the shared result by its own `Prompt` value
 * client-side. Bands load right after page load (client QA 2026-08-14 rev —
 * was IntersectionObserver-deferred; the cards should be ready by the time
 * the visitor scrolls to them).
 *
 * Rendering clones the server-provided <template data-quote-template> per
 * record and fills it via textContent (user-authored text, XSS-safe). On any
 * failure — missing config, network/API error, or zero matching records — the
 * whole element hides (placeholders included) and the band keeps its question
 * + CTA; the cards are decorative, so there is no error copy. While the
 * request is in flight the list shows four pulsing placeholder tiles
 * (server-rendered in plw-story-band.liquid), cleared before rendering.
 *
 * Config via data attributes only (no strings live in JS):
 *   data-airtable-token / data-airtable-base / data-airtable-table
 *   data-prompt — exact Prompt value to match (blank = all approved)
 *   data-max — maxRecords for the request
 */

const AIRTABLE_ENDPOINT = 'https://api.airtable.com/v0';

/**
 * Shared per-page fetches, keyed by base/table/token — all bands on a page
 * use one request. Airtable caps a page at 100 records; approved quotes are
 * curated and well under that, so no pagination.
 * @type {Map<string, Promise<Array<{story: string, prompt: string}>>>}
 */
const sharedFetches = new Map();

/**
 * @param {string} token
 * @param {string} base
 * @param {string} table
 * @returns {Promise<Array<{story: string, prompt: string}>>}
 */
function fetchApproved(token, base, table) {
  const key = `${base}/${table}/${token}`;
  let promise = sharedFetches.get(key);
  if (promise) return promise;

  const params = new URLSearchParams({
    filterByFormula: '{Approved}',
    maxRecords: '100',
  });
  params.append('fields[]', 'Story');
  params.append('fields[]', 'Prompt');

  promise = fetch(`${AIRTABLE_ENDPOINT}/${base}/${encodeURIComponent(table)}?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(async (response) => {
    if (!response.ok) throw new Error(`Airtable ${response.status}`);
    const payload = await response.json();
    return (payload.records ?? [])
      .map((record) => ({
        story: typeof record.fields?.Story === 'string' ? record.fields.Story : '',
        prompt: typeof record.fields?.Prompt === 'string' ? record.fields.Prompt : '',
      }))
      .filter((record) => record.story.trim().length > 0);
  });

  // A failed request shouldn't poison later attempts (e.g. a flaky network).
  promise.catch(() => sharedFetches.delete(key));
  sharedFetches.set(key, promise);
  return promise;
}

class PlwStoryQuotes extends HTMLElement {
  /** Set on disconnect so a late-resolving shared fetch renders nothing. */
  #disconnected = false;

  /** Bound so disconnect can remove a pending load listener. */
  #onPageLoad = () => this.#load();

  connectedCallback() {
    this.#disconnected = false;
    const { airtableToken, airtableBase, airtableTable } = this.dataset;
    if (!airtableToken || !airtableBase || !airtableTable) {
      this.hidden = true;
      return;
    }

    // Load right after the page finishes loading (client QA 2026-08-14:
    // don't wait for the band to near the viewport).
    if (document.readyState === 'complete') {
      this.#load();
    } else {
      window.addEventListener('load', this.#onPageLoad, { once: true });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('load', this.#onPageLoad);
    this.#disconnected = true;
  }

  async #load() {
    const { airtableToken, airtableBase, airtableTable, prompt, max } = this.dataset;

    try {
      const records = await fetchApproved(airtableToken, airtableBase, airtableTable);
      if (this.#disconnected) return;

      // Prompt matches exactly when configured (blank = all approved).
      const stories = records
        .filter((record) => !prompt || record.prompt === prompt)
        .slice(0, Number(max) || 12)
        .map((record) => record.story);

      this.#render(stories);
    } catch {
      this.hidden = true;
    }
  }

  /**
   * @param {string[]} stories - The approved submission texts, in API order.
   */
  #render(stories) {
    const list = this.querySelector('[data-quotes-list]');
    const template = this.querySelector('template[data-quote-template]');
    if (!list || !(template instanceof HTMLTemplateElement) || stories.length === 0) {
      this.hidden = true;
      return;
    }

    // Drop the server-rendered pulsing placeholders before the real cards go in.
    list.replaceChildren();

    const cards = document.createDocumentFragment();
    for (const story of stories) {
      const card = template.content.cloneNode(true);
      const quote = /** @type {DocumentFragment} */ (card).querySelector('[data-quote]');
      if (!quote) continue;
      quote.textContent = story.trim();
      cards.appendChild(card);
    }

    list.appendChild(cards);
  }
}

if (!customElements.get('plw-story-quotes')) {
  customElements.define('plw-story-quotes', PlwStoryQuotes);
}
